import { FC } from 'react';
import { Button, InlineLoading, InlineNotification, Tag } from '@carbon/react';
import { Close } from '@carbon/icons-react';
import { useHistory } from 'react-router-dom';
import {
  wellWorkspacePath,
  type WellWorkspaceLocationState,
} from 'pages/well-workspaces/utils';
import { parseLocalDateTime } from 'utils/dates';
import { useWellWorkspaces } from 'pages/well-workspaces/hooks';
import styles from './workspaces-drawer.module.scss';

interface Props {
  uwi: string;
  open: boolean;
  onClose: () => void;
}

/**
 * The Well page's Workspaces drawer: every well workspace this well belongs to, each
 * opening the workspace it names.
 *
 * Reached from the header's View menu, one drawer per workspace type; this is the Well
 * Workspaces one. The notice below appears only against a backend still serving the
 * older latest-plus-promoted pair, so a partial list never reads as complete.
 * @param {Props} props The well, and whether the drawer is open.
 * @return {JSX.Element | null} The drawer.
 */
export const WorkspacesDrawer: FC<Props> = ({ uwi, open, onClose }) => {
  // Navigates to the workspace's own URL, naming this well in router state, so the reader
  // lands on the well they came from and Back returns here. Create on this page and Map
  // Query's listing navigate the same way, so opening a workspace behaves one way.
  const history = useHistory<WellWorkspaceLocationState>();
  // Held open-only: the drawer is the only reader, so there is nothing to prefetch for
  // and a closed drawer should not keep a query warm.
  const { data, isPending, isError } = useWellWorkspaces(
    open ? uwi : undefined
  );
  const workspaces = data?.workspaces ?? [];

  if (!open) return null;

  return (
    <aside className={styles.drawer} aria-label="Well workspaces">
      <div className={styles.header}>
        <h3 className={styles.title}>Well Workspaces</h3>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Close}
          iconDescription="Close"
          onClick={onClose}
        />
      </div>

      <div className={styles.body}>
        {isPending ? <InlineLoading description="Loading workspaces…" /> : null}

        {isError ? (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle="Could not load this well's workspaces."
            lowContrast
            hideCloseButton
          />
        ) : null}

        {!isPending && !isError && workspaces.length === 0 ? (
          <p className={styles.empty}>
            This well is not in any well workspace yet. Use Action → Create Well
            Workspace to make one.
          </p>
        ) : null}

        {workspaces.map((workspace) => (
          <button
            type="button"
            key={workspace.id}
            className={styles.row}
            onClick={() => {
              onClose();
              // At its own URL, landing on this well rather than the workspace's first.
              history.push(wellWorkspacePath(workspace.id), { well: uwi });
            }}
          >
            <span className={styles.rowName}>{workspace.name}</span>
            {/* The mock's row: state, author, then created/updated — the same three
                lines the Wellbore Geometry drawer beside this one draws, so a workspace
                reads the same whichever type it is. */}
            <span className={styles.rowMeta}>
              {workspace.promoted ? (
                <Tag type="green" size="sm">
                  Promoted
                </Tag>
              ) : (
                <Tag type="cool-gray" size="sm">
                  Draft
                </Tag>
              )}
              {workspace.createdBy ? `Created by ${workspace.createdBy}` : null}
            </span>
            {workspace.createdAt ? (
              <span className={styles.rowMeta}>
                Created: {parseLocalDateTime(workspace.createdAt, true)}
                {workspace.updatedAt
                  ? ` | Updated: ${parseLocalDateTime(
                      workspace.updatedAt,
                      true
                    )}`
                  : null}
              </span>
            ) : null}
          </button>
        ))}

        {/* Only while the endpoint is serving the partial pair. Once it serves `all`
            the list is every workspace for this well, and a caveat would be a lie. */}
        {!isPending && !isError && workspaces.length > 0 && !data?.complete ? (
          <p className={styles.note}>
            Showing this well&rsquo;s most recent workspace and its most
            recently promoted one. The full list needs the links endpoint to
            serve every workspace per well.
          </p>
        ) : null}
      </div>
    </aside>
  );
};
